import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import Peer from "peerjs";
import { makeStyles } from "@mui/styles";
import { RoomStyle } from "./RoomStyle";
import { IconButton, Fade, Box } from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import Backdrop from "@mui/material/Backdrop";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";

import useApp from "../../store/contexts/AppContext";
import UserWebcamSettings from "./UserWebcamSettings";
import TopVideoWebCam from "./TopVideoWebCam";
import VideoPlayerWithCustomControls from "./VideoElement";
import {
  getParticipantData,
  getTeamCode,
  getTeamData,
} from "../../utils/RoomHelpers";
import SOCKET_EVENTS from "../../config/SocketEvents";
import { ONLINE_STATUS, SNACKBAR_VARIANT } from "../../config";
import capitalizeEachFirstWord from "../../utils/CapLetters";
import { ROUTES } from "../../routes/User/paths";
import ToolboxScreenshotPopup from "../../components/Toolbox/ScreenshotPopup";
import ParticipantVideo from "./ParticipantVideo";
import "../../styles/reactionEmoji.css";

const useStyles = makeStyles((theme) => RoomStyle(theme));

const useStyles2 = makeStyles((theme) => ({
  customIconButton: {
    fontSize: "25px",
    color: "#C8B758",
    transition: "background-color 0.5s ease-in-out",
    "&:hover": {
      color: "#FFD700",
      transition: "background-color 0.5s ease-in-out",
    },
  },
}));

const styleForReactionModal = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 300,
  bgcolor: "#313131",
  color: "#fff",
  border: "2px solid #000",
  boxShadow: 24,
  p: 2,
};

/**
 * Polyfill for getUserMedia to support older browsers
 */
const polyfillGetUserMedia = () => {
  // Older browsers might not have mediaDevices at all
  if (navigator.mediaDevices === undefined) {
    navigator.mediaDevices = {};
  }

  // Some browsers partially implement mediaDevices
  if (navigator.mediaDevices.getUserMedia === undefined) {
    navigator.mediaDevices.getUserMedia = function (constraints) {
      // First get the legacy getUserMedia
      const getUserMedia =
        navigator.getUserMedia ||
        navigator.webkitGetUserMedia ||
        navigator.mozGetUserMedia ||
        navigator.msGetUserMedia;

      // If no getUserMedia support at all
      if (!getUserMedia) {
        return Promise.reject(
          new Error("getUserMedia is not implemented in this browser")
        );
      }

      // Otherwise, wrap the legacy getUserMedia with a Promise
      return new Promise((resolve, reject) => {
        getUserMedia.call(navigator, constraints, resolve, reject);
      });
    };
  }
};

/**
 * Check if media devices are supported
 */
const isMediaDevicesSupported = () => {
  return !!(
    navigator.mediaDevices?.getUserMedia ||
    navigator.getUserMedia ||
    navigator.webkitGetUserMedia ||
    navigator.mozGetUserMedia ||
    navigator.msGetUserMedia
  );
};

/**
 * Get browser information
 */
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  let browserName = "Unknown";
  let browserVersion = "Unknown";

  if (ua.indexOf("Firefox") > -1) {
    browserName = "Firefox";
    browserVersion = ua.match(/Firefox\/([0-9.]+)/)?.[1] || "Unknown";
  } else if (ua.indexOf("Chrome") > -1) {
    browserName = "Chrome";
    browserVersion = ua.match(/Chrome\/([0-9.]+)/)?.[1] || "Unknown";
  } else if (ua.indexOf("Safari") > -1) {
    browserName = "Safari";
    browserVersion = ua.match(/Version\/([0-9.]+)/)?.[1] || "Unknown";
  } else if (ua.indexOf("Edge") > -1 || ua.indexOf("Edg") > -1) {
    browserName = "Edge";
    browserVersion = ua.match(/Edg\/([0-9.]+)/)?.[1] || "Unknown";
  } else if (ua.indexOf("Opera") > -1 || ua.indexOf("OPR") > -1) {
    browserName = "Opera";
    browserVersion = ua.match(/OPR\/([0-9.]+)/)?.[1] || "Unknown";
  }

  return { browserName, browserVersion, userAgent: ua };
};

// Cross-browser getUserMedia implementation
const getCompatibleUserMedia = async (constraints) => {
  // Apply polyfill first
  polyfillGetUserMedia();

  // Check if any form of getUserMedia is supported
  if (!isMediaDevicesSupported()) {
    throw new Error(
      "getUserMedia is not supported in this browser. Please use a modern browser like Chrome, Firefox, Safari, or Edge."
    );
  }

  try {
    // Try modern API first
    if (navigator.mediaDevices?.getUserMedia) {
      return await navigator.mediaDevices.getUserMedia(constraints);
    }

    // Fallback to legacy API
    const getUserMedia =
      navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia;

    if (getUserMedia) {
      return new Promise((resolve, reject) => {
        getUserMedia.call(navigator, constraints, resolve, reject);
      });
    }

    throw new Error("No getUserMedia implementation found");
  } catch (err) {
    console.error("Error accessing media devices:", err);

    // Provide more helpful error messages
    if (
      err.name === "NotAllowedError" ||
      err.name === "PermissionDeniedError"
    ) {
      throw new Error(
        "Camera/microphone permission denied. Please allow access in your browser settings."
      );
    } else if (
      err.name === "NotFoundError" ||
      err.name === "DevicesNotFoundError"
    ) {
      throw new Error(
        "No camera or microphone found. Please connect a device and try again."
      );
    } else if (
      err.name === "NotReadableError" ||
      err.name === "TrackStartError"
    ) {
      throw new Error(
        "Camera/microphone is already in use by another application."
      );
    } else if (
      err.name === "OverconstrainedError" ||
      err.name === "ConstraintNotSatisfiedError"
    ) {
      throw new Error(
        "Camera/microphone does not meet the required constraints."
      );
    } else if (err.name === "TypeError") {
      throw new Error("Invalid media constraints. Please check your settings.");
    } else if (err.name === "SecurityError") {
      throw new Error(
        "Media access blocked due to security restrictions. Please use HTTPS."
      );
    }

    throw err;
  }
};

/**
 * Get available media devices with cross-browser support
 */
const getCompatibleMediaDevices = async () => {
  polyfillGetUserMedia();

  try {
    if (navigator.mediaDevices?.enumerateDevices) {
      return await navigator.mediaDevices.enumerateDevices();
    }

    // Fallback for older browsers
    if (MediaStreamTrack?.getSources) {
      return new Promise((resolve) => {
        MediaStreamTrack.getSources((sources) => {
          resolve(sources);
        });
      });
    }

    // If no enumeration is available, return empty array
    console.warn("Device enumeration not supported");
    return [];
  } catch (err) {
    console.error("Error enumerating devices:", err);
    return [];
  }
};

/**
 * Generate random position offset for emoji reactions
 */
const getRandomOffset = () => {
  return Math.random() * 100 - 50; // Random value between -50 and 50
};

/**
 * Generate random animation duration
 */
const getRandomDuration = () => {
  return 3.5 + Math.random() * 1.5; // Between 3.5s and 5s
};

const UserWebcam = React.memo(
  ({
    roomLoading,
    setShowRoomTitle,
    showRoomTitle,
    setShowRoomParticipants,
    showRoomParticipants,
    setShowRoomTeams,
    showRoomTeams,
    isCurrentUserMainAdmin,
    roomId,
    setRoomCallParticipants,
    roomCallParticipants,
    isUserOnSpectatorMode,
  }) => {
    const {
      appState: { activeChat, userData, socket },
      showSnackbar,
      loadANewPage,
      reactToRoomScreenshotApiCall,
    } = useApp();

    const classes = useStyles({ showRoomTitle });
    const classes2 = useStyles2();
    const peerRef = useRef();
    const myVideoRef = useRef(null);
    const myVideoSettingsRef = useRef(null);
    const myVideoStream = useRef();
    const videoGridRef = useRef(null);
    const participantVideoRefs = useRef({});

    const [mediaSupported, setMediaSupported] = useState(true);
    const [userTeamName, setUserTeamName] = useState(null);
    const [userTeamCode, setUserTeamCode] = useState(null);
    const [userTeamId, setUserTeamId] = useState(null);
    const [isVideoOn, setIsVideoOn] = useState(true);
    const [isAudioOn, setIsAudioOn] = useState(true);
    const [anchorWebcamMenuEl, setAnchorWebcamMenuEl] = useState(null);
    const [userWebcamSettingsOpen, setUserWebcamSettingsOpen] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [showMyVisual, setShowMyVisual] = useState(true);
    const [selectedAudioInputDevice, setSelectedAudioInputDevice] =
      useState("");
    const [selectedAudioOutputDevice, setSelectedAudioOutputDevice] =
      useState("");
    const [selectedVideoDevice, setSelectedVideoDevice] = useState("");
    const [reactionIconExpanded, setReactionIconExpanded] = useState(false);
    const [allParticipantsList, setAllParticipantsList] = useState({});
    const [allParticipantsVideos, setAllParticipantsVideos] = useState([]);
    const [activeTeamParticipantVideos, setActiveTeamParticipantVideos] =
      useState([]);
    const [reactionEmojis, setReactionEmojis] = useState([]);
    const [toolboxScreenshotsData, setToolboxScreenshotsData] = useState([]);
    const [currentToolboxScreenshotIndex, setCurrentToolboxScreenshotIndex] =
      useState(0);
    const [countdown, setCountdown] = useState(0);
    const [toolboxScreenshotsQueue, setToolboxScreenshotsQueue] = useState([]);
    const [currentToolboxScreenshot, setCurrentToolboxScreenshot] =
      useState(null);
    const [globalCountdown, setGlobalCountdown] = useState(0);
    const [activeScreenshotId, setActiveScreenshotId] = useState(null);
    const [selectedReactionData, setSelectedReactionData] = useState({});
    const [openReactionModal, setOpenReactionModal] = useState(false);

    const roomTeams = activeChat.room_teams;
    const currentUserTeamScore =
      roomTeams?.find((team) => team.id?.toString() === userTeamId?.toString())
        ?.score ?? 0;

    const handleOpenReactionModal = useCallback((participantData) => {
      setSelectedReactionData(participantData);
      setOpenReactionModal(true);
    }, []);

    const handleCloseReactionModal = () => {
      setOpenReactionModal(false);
      setTimeout(() => {
        setSelectedReactionData({});
      }, 500);
    };

    const processQueue = useCallback(() => {
      const serverTime = Date.now() + (window.serverTimeDiff || 0);

      setToolboxScreenshotsQueue((prevQueue) => {
        if (prevQueue.length === 0) {
          setCurrentToolboxScreenshot(null);
          setActiveScreenshotId(null);
          setGlobalCountdown(0);
          return prevQueue;
        }

        const [activeScreenshot, ...remainingQueue] = prevQueue;

        if (activeScreenshot.id !== activeScreenshotId) {
          setCurrentToolboxScreenshot(activeScreenshot);
          setActiveScreenshotId(activeScreenshot.id);
          setGlobalCountdown(activeScreenshot.duration_in_secs);
        }

        if (serverTime >= activeScreenshot.end_timestamp) {
          return remainingQueue;
        }

        return prevQueue;
      });
    }, [activeScreenshotId]);

    const memoizedAllParticipantsVideos = useMemo(() => {
      return allParticipantsVideos
        .map((participantData) => {
          const participantId = participantData.userId?.toString();

          if (
            participantData.isActiveOnCall &&
            userData.id?.toString() !== participantId
          ) {
            let participantScreenshot = null;
            if (currentToolboxScreenshot) {
              if (
                currentToolboxScreenshot.has_team_selected &&
                currentToolboxScreenshot.receiver_id?.toString() ===
                  participantData.userId?.toString()
              ) {
                participantScreenshot = currentToolboxScreenshot;
              }
            }

            const participantTeamData = activeChat?.room_teams?.find(
              (team, index) =>
                team.id?.toString() === participantData.userTeamId?.toString()
            );

            participantData = {
              ...participantData,
              userTeamScore: participantTeamData?.score ?? "admin",
            };

            return (
              <ParticipantVideo
                roomId={roomId}
                key={`${participantData.userId}-${participantData.isActiveOnCall}`}
                participantData={participantData}
                currentToolboxScreenshot={participantScreenshot}
                isUserOnSpectatorMode={isUserOnSpectatorMode}
                isParticipantOnSpectatorMode={
                  participantData.userIsOnSpectatorMode
                }
                handleOpenReactionModal={handleOpenReactionModal}
                classes={classes}
                reactToRoomScreenshotApiCall={reactToRoomScreenshotApiCall}
                countdown={globalCountdown}
                onCountdownEnd={processQueue}
                socket={socket}
                userData={userData}
                userTeamId={userTeamId}
              />
            );
          }
          return null;
        })
        .filter(Boolean);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      allParticipantsVideos,
      userData.id,
      currentToolboxScreenshot,
      isUserOnSpectatorMode,
      handleOpenReactionModal,
      classes,
      globalCountdown,
      processQueue,
      activeChat?.room_teams,
    ]);

    const createPeer = () => {
      const host = process.env.REACT_APP_PEER_HOST;
      const port = process.env.REACT_APP_PEER_PORT
        ? parseInt(process.env.REACT_APP_PEER_PORT, 10)
        : undefined;
      const path = "/";
      const secure = process.env.REACT_APP_PEER_SECURE === "true";

      if (!host) {
        console.error("Missing PeerJS host configuration");
        return null;
      }

      const peer = new Peer(Math.random().toString(36).substr(2, 9), {
        host,
        port,
        path,
        secure,
        config: {
          iceServers: [
            { urls: "stun:stun01.sipphone.com" },
            { urls: "stun:stun.ekiga.net" },
            { urls: "stun:stunserver.org" },
            { urls: "stun:stun.softjoys.com" },
            { urls: "stun:stun.voiparound.com" },
            { urls: "stun:stun.voipbuster.com" },
            { urls: "stun:stun.voipstunt.com" },
            { urls: "stun:stun.voxgratia.org" },
            { urls: "stun:stun.xten.com" },
            {
              urls: "turn:192.158.29.39:3478?transport=udp",
              credential: "JZEOEt2V3Qb0y27GRntt2u2PAYA=",
              username: "28224511:1379330808",
            },
            {
              urls: "turn:192.158.29.39:3478?transport=tcp",
              credential: "JZEOEt2V3Qb0y27GRntt2u2PAYA=",
              username: "28224511:1379330808",
            },
          ],
        },
        debug: 0,
      });

      peer.on("error", (error) => {
        console.error("PeerJS Error:", error);
      });

      return peer;
    };

    useEffect(() => {
      // Load saved device preferences from localStorage
      const loadSavedDevices = () => {
        const savedDevices = localStorage.getItem("preferredDevices");

        if (savedDevices) {
          try {
            const { audioInput, audioOutput, video } = JSON.parse(savedDevices);

            console.log("Loading saved devices from localStorage:", {
              audioInput,
              audioOutput,
              video,
            });

            // Only set if the device IDs are not empty
            if (audioInput) {
              console.log("Setting saved audio input device:", audioInput);
              setSelectedAudioInputDevice(audioInput);
            }
            if (audioOutput) {
              console.log("Setting saved audio output device:", audioOutput);
              setSelectedAudioOutputDevice(audioOutput);
            }
            if (video) {
              console.log("Setting saved video device:", video);
              setSelectedVideoDevice(video);
            }
          } catch (err) {
            console.error("Error loading saved devices:", err);
            // If there's an error, clear the corrupted data
            localStorage.removeItem("preferredDevices");
            localStorage.removeItem("mediaSetupComplete");
            localStorage.removeItem("setupTimestamp");
          }
        } else {
          console.log("No saved devices found in localStorage");
        }
      };

      loadSavedDevices();
    }, []);

    const getDevices = async () => {
      try {
        const devices = await getCompatibleMediaDevices();
        const audioInputDevices = devices.filter(
          (device) => device.kind === "audioinput"
        );
        const audioOutputDevices = devices.filter(
          (device) => device.kind === "audiooutput"
        );
        const videoDevices = devices.filter(
          (device) => device.kind === "videoinput"
        );

        console.log("Available devices:", {
          audioInputs: audioInputDevices.length,
          audioOutputs: audioOutputDevices.length,
          videos: videoDevices.length,
        });

        // Check if saved devices are still available
        const savedDevices = localStorage.getItem("preferredDevices");
        let devicePreferences = null;

        if (savedDevices) {
          try {
            devicePreferences = JSON.parse(savedDevices);
            console.log(
              "Checking if saved devices still exist:",
              devicePreferences
            );
          } catch (err) {
            console.error("Error parsing saved devices:", err);
            localStorage.removeItem("preferredDevices");
          }
        }

        // For audio input: use saved -> current selection -> first available
        if (!selectedAudioInputDevice) {
          if (devicePreferences?.audioInput) {
            const deviceExists = audioInputDevices.find(
              (d) => d.deviceId === devicePreferences.audioInput
            );
            if (deviceExists) {
              console.log(
                "Using saved audio input device:",
                devicePreferences.audioInput
              );
              setSelectedAudioInputDevice(devicePreferences.audioInput);
            } else if (audioInputDevices.length > 0) {
              console.log(
                "Saved audio input device not found, using default:",
                audioInputDevices[0].deviceId
              );
              setSelectedAudioInputDevice(audioInputDevices[0].deviceId);
              // Update localStorage with new default
              updateDevicePreference(
                "audioInput",
                audioInputDevices[0].deviceId
              );
            }
          } else if (audioInputDevices.length > 0) {
            console.log(
              "No saved audio input, using first available:",
              audioInputDevices[0].deviceId
            );
            setSelectedAudioInputDevice(audioInputDevices[0].deviceId);
          }
        }

        // For audio output: use saved -> current selection -> first available
        if (!selectedAudioOutputDevice) {
          if (devicePreferences?.audioOutput) {
            const deviceExists = audioOutputDevices.find(
              (d) => d.deviceId === devicePreferences.audioOutput
            );
            if (deviceExists) {
              console.log(
                "Using saved audio output device:",
                devicePreferences.audioOutput
              );
              setSelectedAudioOutputDevice(devicePreferences.audioOutput);
            } else if (audioOutputDevices.length > 0) {
              console.log(
                "Saved audio output device not found, using default:",
                audioOutputDevices[0].deviceId
              );
              setSelectedAudioOutputDevice(audioOutputDevices[0].deviceId);
              updateDevicePreference(
                "audioOutput",
                audioOutputDevices[0].deviceId
              );
            }
          } else if (audioOutputDevices.length > 0) {
            console.log(
              "No saved audio output, using first available:",
              audioOutputDevices[0].deviceId
            );
            setSelectedAudioOutputDevice(audioOutputDevices[0].deviceId);
          }
        }

        // For video: use saved -> current selection -> first available
        if (!selectedVideoDevice) {
          if (devicePreferences?.video) {
            const deviceExists = videoDevices.find(
              (d) => d.deviceId === devicePreferences.video
            );
            if (deviceExists) {
              console.log("Using saved video device:", devicePreferences.video);
              setSelectedVideoDevice(devicePreferences.video);
            } else if (videoDevices.length > 0) {
              console.log(
                "Saved video device not found, using default:",
                videoDevices[0].deviceId
              );
              setSelectedVideoDevice(videoDevices[0].deviceId);
              updateDevicePreference("video", videoDevices[0].deviceId);
            }
          } else if (videoDevices.length > 0) {
            console.log(
              "No saved video, using first available:",
              videoDevices[0].deviceId
            );
            setSelectedVideoDevice(videoDevices[0].deviceId);
          }
        }
      } catch (error) {
        console.error("Error enumerating devices:", error);
        showSnackbar("Error accessing media devices");
      }
    };

    // Helper function to update a single device preference
    const updateDevicePreference = (deviceType, deviceId) => {
      try {
        const savedDevices = localStorage.getItem("preferredDevices");
        let preferences = {};

        if (savedDevices) {
          preferences = JSON.parse(savedDevices);
        }

        preferences[deviceType] = deviceId;

        localStorage.setItem("preferredDevices", JSON.stringify(preferences));
        localStorage.setItem("setupTimestamp", Date.now().toString());
        console.log(
          `Updated ${deviceType} preference in localStorage:`,
          deviceId
        );
      } catch (err) {
        console.error("Error updating device preference:", err);
      }
    };

    // Listen for device changes (plug/unplug)
    useEffect(() => {
      const handleDeviceChange = async () => {
        console.log("Device change detected (device plugged/unplugged)");

        // Re-enumerate devices
        await getDevices();

        // Check if current devices are still available
        const devices = await getCompatibleMediaDevices();
        const audioInputs = devices.filter((d) => d.kind === "audioinput");
        const audioOutputs = devices.filter((d) => d.kind === "audiooutput");
        const videos = devices.filter((d) => d.kind === "videoinput");

        // Check if selected audio input still exists
        if (selectedAudioInputDevice) {
          const audioInputExists = audioInputs.find(
            (d) => d.deviceId === selectedAudioInputDevice
          );
          if (!audioInputExists) {
            console.log("Selected audio input device disconnected");
            if (audioInputs.length > 0) {
              setSelectedAudioInputDevice(audioInputs[0].deviceId);
              updateDevicePreference("audioInput", audioInputs[0].deviceId);
              showSnackbar("Microphone disconnected, switched to default");
            } else {
              showSnackbar("No microphone available", SNACKBAR_VARIANT.ERROR);
            }
          }
        }

        // Check if selected audio output still exists
        if (selectedAudioOutputDevice) {
          const audioOutputExists = audioOutputs.find(
            (d) => d.deviceId === selectedAudioOutputDevice
          );
          if (!audioOutputExists) {
            console.log("Selected audio output device disconnected");
            if (audioOutputs.length > 0) {
              setSelectedAudioOutputDevice(audioOutputs[0].deviceId);
              updateDevicePreference("audioOutput", audioOutputs[0].deviceId);
              showSnackbar("Speaker disconnected, switched to default");
            } else {
              showSnackbar("No speaker available", SNACKBAR_VARIANT.ERROR);
            }
          }
        }

        // Check if selected video device still exists
        if (selectedVideoDevice) {
          const videoExists = videos.find(
            (d) => d.deviceId === selectedVideoDevice
          );
          if (!videoExists) {
            console.log("Selected video device disconnected");
            if (videos.length > 0) {
              setSelectedVideoDevice(videos[0].deviceId);
              updateDevicePreference("video", videos[0].deviceId);
              showSnackbar("Camera disconnected, switched to default");
            } else {
              showSnackbar("No camera available", SNACKBAR_VARIANT.ERROR);
              // Redirect to spectator mode if no camera
              if (!isUserOnSpectatorMode) {
                showSnackbar("No camera found, switching to spectator mode");
                setTimeout(() => {
                  loadANewPage(`${ROUTES.spectate}/${roomId}`);
                }, 2000);
              }
            }
          }
        }
      };

      // Add event listener for device changes
      if (navigator.mediaDevices?.addEventListener) {
        navigator.mediaDevices.addEventListener(
          "devicechange",
          handleDeviceChange
        );
      }

      return () => {
        // Cleanup event listener
        if (navigator.mediaDevices?.removeEventListener) {
          navigator.mediaDevices.removeEventListener(
            "devicechange",
            handleDeviceChange
          );
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      selectedAudioInputDevice,
      selectedAudioOutputDevice,
      selectedVideoDevice,
      showSnackbar,
      isUserOnSpectatorMode,
      roomId,
      loadANewPage,
    ]);

    const initializeMedia = async () => {
      // Apply polyfill for older browsers
      polyfillGetUserMedia();

      // Check browser support
      if (!isMediaDevicesSupported()) {
        const browserInfo = getBrowserInfo();
        showSnackbar(
          `Your browser (${browserInfo.browserName}) does not support video calls. Please use a modern browser like Chrome, Firefox, Safari, or Edge.`
        );
        setMediaSupported(false);
        return;
      }

      try {
        const constraints = {
          audio: {
            deviceId: selectedAudioInputDevice
              ? { exact: selectedAudioInputDevice }
              : undefined,
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: {
            deviceId: selectedVideoDevice
              ? { exact: selectedVideoDevice }
              : undefined,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        };

        const stream = await getCompatibleUserMedia(constraints);

        myVideoStream.current = stream;
        if (myVideoRef.current) {
          myVideoRef.current.srcObject = stream;
          // Explicitly play the video for better browser compatibility
          try {
            await myVideoRef.current.play();
          } catch (playError) {
            console.warn("Autoplay prevented:", playError);
          }
        }
        if (myVideoSettingsRef.current) {
          myVideoSettingsRef.current.srcObject = stream;
          try {
            await myVideoSettingsRef.current.play();
          } catch (playError) {
            console.warn("Settings video autoplay prevented:", playError);
          }
        }

        if (!isAudioOn) {
          const audioTrack = stream.getAudioTracks()[0];
          if (audioTrack) {
            audioTrack.enabled = false;
          }
        }

        if (!isVideoOn) {
          const videoTrack = stream.getVideoTracks()[0];
          if (videoTrack) {
            videoTrack.enabled = false;
          }
        }

        await getDevices();

        // Initialize peer connection after successful media setup
        peerRef.current = createPeer();

        if (peerRef.current) {
          peerRef.current.on("open", (id) => {
            const roomData = {
              online_status: ONLINE_STATUS.ONLINE,
              user: {
                participant_id: userData.id,
                username: userData.username,
                peer_id: id,
                user_team_name: userTeamName,
                user_team_code: userTeamCode,
                isOnSpectatorMode: isUserOnSpectatorMode,
              },
            };

            socket.emit(SOCKET_EVENTS.USER_JOINED_GROUP_ROOM, roomId, roomData);

            socket.on(
              SOCKET_EVENTS.USER_CONNECTED_IN_GROUP_ROOM,
              (socketUserData) => {
                const peerId = socketUserData.peer_id;

                peerRef.current.call(peerId, stream, {
                  metadata: {
                    userId: userData.id,
                    username: userData.username,
                    participantTeamCode: userTeamCode,
                    participantTeamName: userTeamName,
                    isOnSpectatorMode: isUserOnSpectatorMode,
                  },
                });
              }
            );
          });

          peerRef.current.on("call", (call) => {
            const {
              userId,
              username,
              participantTeamCode,
              participantTeamName,
              isOnSpectatorMode,
            } = call.metadata;

            call.answer(myVideoStream.current);

            call.on("stream", (userVideoStream) => {
              const userCallData = {
                userId,
                username,
                stream: userVideoStream,
                participantTeamCode,
                participantTeamName,
                isOnSpectatorMode,
                participantTeamId:
                  roomTeams?.find((team) => team.name === participantTeamName)
                    ?.id || "admin",
              };
              addRoomParticipantToCall(userCallData);
            });
          });
        }
      } catch (err) {
        console.error("Media initialization error:", err);

        // User-friendly error messages
        let errorMessage = "Error accessing media devices";

        if (err.message) {
          errorMessage = err.message;
        }

        showSnackbar(errorMessage);
        handleMediaError(err);
      }
    };

    const handleMediaError = (error) => {
      console.error("Media Error:", error);
      // If already in spectator mode, don't redirect
      if (!isUserOnSpectatorMode) {
        loadANewPage(`${ROUTES.spectate}/${roomId}`);
      }
    };

    const handleReactionIconExpandClick = () => {
      setReactionIconExpanded(!reactionIconExpanded);
    };

    const handleReactionIcon = (emoji, isSentToUser) => {
      const id = Date.now() + Math.random(); // More unique ID
      const iconData = {
        room_id: roomId,
        emoji,
        id,
        username: capitalizeEachFirstWord(userData.username),
        isSentToUser,
        receiverUsername: capitalizeEachFirstWord(
          selectedReactionData.username
        ),
        // Add positioning data for more dynamic animations
        offsetX: getRandomOffset(),
        duration: getRandomDuration(),
        animationType: Math.random() > 0.5 ? "left" : "right", // Random direction
      };

      socket.emit(SOCKET_EVENTS.SEND_EMOJI_REACTION_IN_GROUP_ROOM, iconData);

      // Don't add locally - let the socket broadcast handle it
      // This prevents duplicates
    };

    const handleSendToolboxScreenshot = useCallback(
      (screenshot, durationInSecs, hasTeamSelected, userTeam) => {
        const serverTime = Date.now() + (window.serverTimeDiff || 0);
        const screenshotData = {
          id: Date.now(),
          room_id: roomId,
          screenshot,
          username: capitalizeEachFirstWord(userData.username),
          receiver_id: hasTeamSelected
            ? userTeam?.team_representing_user_id
            : userData.id,
          duration_in_secs: Number(durationInSecs),
          has_team_selected: hasTeamSelected,
          user_team: userTeam,
          start_timestamp: serverTime + 2000,
          end_timestamp: serverTime + 2000 + Number(durationInSecs) * 1000,
        };

        socket.emit(
          SOCKET_EVENTS.SEND_TOOLBOX_SCREENSHOT_IN_GROUP_ROOM,
          screenshotData
        );
      },
      [roomId, userData.id, userData.username, socket]
    );

    const closeToolboxScreenshot = useCallback((toolboxScreenshotId) => {
      setToolboxScreenshotsData((prev) => {
        const updatedScreenshots = prev.filter(
          (screenshotData) => screenshotData.id !== toolboxScreenshotId
        );
        if (updatedScreenshots.length > 0) {
          setCurrentToolboxScreenshotIndex((prevIndex) => {
            const newIndex =
              prevIndex >= updatedScreenshots.length ? 0 : prevIndex;
            setCurrentToolboxScreenshot(updatedScreenshots[newIndex]);
            return newIndex;
          });
        }
        return updatedScreenshots;
      });
    }, []);

    const handleWebcamMenuClick = (event) => {
      setAnchorWebcamMenuEl(event.currentTarget);
    };

    const handleWebcamMenuClose = () => {
      setAnchorWebcamMenuEl(null);
    };

    const openUserWebcamSettingsModal = () => {
      handleWebcamMenuClose();
      setUserWebcamSettingsOpen(true);
    };

    const handleRoomTitleToggle = () => {
      setShowRoomTitle((prev) => !prev);
    };

    const handleRoomParticipantsVisibility = () => {
      setShowRoomParticipants((prev) => !prev);
    };

    const handleRoomTeamsVisibility = () => {
      setShowRoomTeams((prev) => !prev);
    };

    const handleVideoToggle = () => {
      if (myVideoStream.current) {
        const videoTrack = myVideoStream.current.getVideoTracks()[0];
        if (videoTrack) {
          const enabled = videoTrack.enabled;
          videoTrack.enabled = !enabled;
          setIsVideoOn(!enabled);
        }
      } else {
        showSnackbar(
          "Please allow access to camera and microphone, then refresh the page"
        );
      }
    };

    const handleAudioToggle = () => {
      if (myVideoStream.current) {
        const audioTrack = myVideoStream.current.getAudioTracks()[0];
        if (audioTrack) {
          const enabled = audioTrack.enabled;
          audioTrack.enabled = !enabled;
          setIsAudioOn(!enabled);
        }
      } else {
        showSnackbar(
          "Please allow access to camera and microphone, then refresh the page"
        );
      }
    };

    const addRoomParticipantToCall = useCallback(
      (data) => {
        const {
          stream,
          userId,
          username,
          participantTeamCode,
          participantTeamName,
          isOnSpectatorMode,
          participantTeamId,
        } = data;

        const newParticipant = {
          userId,
          username,
          userTeamCode: participantTeamCode,
          userTeamName: participantTeamName,
          userTeamId: participantTeamId,
          userIsOnSpectatorMode: isOnSpectatorMode,
          videoComponent: (
            <VideoPlayerWithCustomControls
              url={stream}
              className={`${classes.videoCam} ${participantTeamCode} otherParticipants`}
            />
          ),
        };

        if (!isOnSpectatorMode) {
          setActiveTeamParticipantVideos((prevParticipants) => {
            const existingUserIndex = prevParticipants.findIndex(
              (participant) =>
                participant.userTeamCode?.toString() ===
                newParticipant.userTeamCode?.toString()
            );

            const captureScreenshot = (callback) => {
              const video = document.createElement("video");
              video.srcObject = stream;
              video.play();

              video.onloadedmetadata = () => {
                const canvas = document.createElement("canvas");
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                canvas
                  .getContext("2d")
                  .drawImage(video, 0, 0, canvas.width, canvas.height);
                const screenshot = canvas.toDataURL("image/jpeg");
                callback(screenshot);
              };
            };

            if (existingUserIndex === -1) {
              const updatedParticipants = [...prevParticipants];

              captureScreenshot((screenshot) => {
                updatedParticipants.push({
                  userId,
                  userTeamCode: newParticipant.userTeamCode,
                  userTeamName: newParticipant.userTeamName,
                  screenshot: screenshot,
                });

                setActiveTeamParticipantVideos(updatedParticipants);
              });

              return prevParticipants;
            } else {
              return prevParticipants.map((participant, index) => {
                if (index === existingUserIndex) {
                  captureScreenshot((screenshot) => {
                    setActiveTeamParticipantVideos((prevState) =>
                      prevState.map((p, i) =>
                        i === index ? { ...p, screenshot: screenshot } : p
                      )
                    );
                  });
                  return participant;
                }
                return participant;
              });
            }
          });
        }

        setAllParticipantsVideos((prevParticipants) => {
          const existingUserIndex = prevParticipants.findIndex(
            (participant) =>
              participant.userId?.toString() === userId?.toString()
          );

          if (existingUserIndex !== -1) {
            const updatedParticipants = [...prevParticipants];
            updatedParticipants[existingUserIndex] = {
              ...updatedParticipants[existingUserIndex],
              videoComponent: (
                <VideoPlayerWithCustomControls
                  url={stream}
                  className={`${classes.videoCam} ${participantTeamCode} otherParticipants`}
                />
              ),
            };
            return updatedParticipants;
          }

          const isAnotherUserInMyTeamActive = prevParticipants.some(
            (participant) =>
              participant.userTeamCode?.toString() ===
                participantTeamCode?.toString() && participant.isActiveOnCall
          );

          newParticipant.isActiveOnCall = !isAnotherUserInMyTeamActive;

          if (!participantVideoRefs.current[userId]) {
            return [...prevParticipants, newParticipant];
          }
          return prevParticipants;
        });
      },
      [classes.videoCam]
    );

    const updateRoomParticipantCall = useCallback(
      async (roomTeams) => {
        setAllParticipantsVideos((prevParticipants) => {
          const updatedParticipants = [...prevParticipants];

          roomTeams.forEach((roomTeamFromSocket) => {
            const roomTeamSelectedVideoUserId =
              roomTeamFromSocket.current_video_selected_participant?.toString();
            const participantIndex = updatedParticipants.findIndex(
              (participant) =>
                participant.userId?.toString() === roomTeamSelectedVideoUserId
            );

            if (participantIndex !== -1) {
              const participant = updatedParticipants[participantIndex];
              if (!participant.isActiveOnCall) {
                const teamParticipants = updatedParticipants.filter(
                  (p) =>
                    p.userTeamCode?.toString() ===
                    participant.userTeamCode?.toString()
                );

                teamParticipants.forEach((p) => {
                  p.isActiveOnCall =
                    p.userId?.toString() === roomTeamSelectedVideoUserId;
                });

                if (
                  userData.id?.toString() ===
                    roomTeamSelectedVideoUserId?.toString() ||
                  isCurrentUserMainAdmin
                ) {
                  setShowMyVisual(true);
                } else {
                  setShowMyVisual(false);
                }
              }
            }
          });

          return updatedParticipants;
        });
      },
      [userData.id, isCurrentUserMainAdmin]
    );

    const handleUserConnected = useCallback(
      (socketUserData) => {
        const {
          peer_id: peerId,
          participant_id: userId,
          username,
          user_team_code: participantTeamCode,
          user_team_name: participantTeamName,
          isOnSpectatorMode,
        } = socketUserData;

        if (participantVideoRefs.current[userId]) {
          return;
        }

        setAllParticipantsList((prev) => {
          if (!prev[userId]) {
            const updatedParticipantsList = {
              ...prev,
              [userId]: { id: userId, isOnSpectatorMode },
            };

            setTimeout(() => {
              let joinMessage = `${capitalizeEachFirstWord(
                username
              )} joined the call`;
              if (isOnSpectatorMode) {
                joinMessage += " on spectator mode";
              }
              showSnackbar(joinMessage);
            }, 1000);

            return updatedParticipantsList;
          }
          return prev;
        });

        if (peerRef.current && myVideoStream.current) {
          const optionsData = {
            metadata: {
              userId,
              username,
              participantTeamCode,
              participantTeamName,
            },
          };

          const call = peerRef.current.call(
            peerId,
            myVideoStream.current,
            optionsData
          );
          call.on("stream", (userVideoStream) => {
            const userCallData = {
              userId,
              stream: userVideoStream,
              participantTeamCode,
              participantTeamName,
              username,
              isOnSpectatorMode,
              participantTeamId:
                roomTeams?.find((team) => team.name === participantTeamName)
                  ?.id || "admin",
            };
            addRoomParticipantToCall(userCallData);
          });
        }
      },
      [showSnackbar, addRoomParticipantToCall, roomTeams]
    );

    const handleUserDisconnected = useCallback(
      (socketUserData) => {
        const { participant_id: userId, username } = socketUserData;
        const capitalizedUsername = capitalizeEachFirstWord(username);

        if (participantVideoRefs.current[userId]) {
          delete participantVideoRefs.current[userId];
        }

        setAllParticipantsList((prev) => {
          if (prev[userId]) {
            const updatedParticipantsList = { ...prev };
            delete updatedParticipantsList[userId];
            showSnackbar(`${capitalizedUsername} left the call`);
            return updatedParticipantsList;
          }
          return prev;
        });

        setAllParticipantsVideos((prev) =>
          prev.filter(
            (participant) =>
              participant.userId?.toString() !== userId?.toString()
          )
        );

        setActiveTeamParticipantVideos((prev) =>
          prev.filter(
            (participant) =>
              participant.userId?.toString() !== userId?.toString()
          )
        );
      },
      [showSnackbar]
    );

    useEffect(() => {
      if (
        Object.keys(userData).length > 0 &&
        Object.keys(activeChat).length > 0
      ) {
        const roomParticipantData = getParticipantData(
          activeChat.room_participants,
          userData.id
        );

        let userTeamCodeResult;
        let userTeamNameValue;
        let userTeamIdValue;

        if (roomParticipantData) {
          const roomParticipantTeamId = isUserOnSpectatorMode
            ? null
            : roomParticipantData.room_team_id;

          userTeamCodeResult =
            roomParticipantData.is_admin === 1
              ? "admin"
              : isUserOnSpectatorMode
              ? `team-spectator`
              : getTeamCode(roomTeams, roomParticipantTeamId);

          const userTeamDataResult =
            roomParticipantData.is_admin === 1
              ? "admin"
              : isUserOnSpectatorMode
              ? `team-spectator`
              : getTeamData(roomTeams, roomParticipantTeamId);

          userTeamNameValue =
            typeof userTeamDataResult === "string"
              ? userTeamDataResult
              : userTeamDataResult.name;
          userTeamIdValue =
            typeof userTeamDataResult === "string"
              ? userTeamDataResult
              : userTeamDataResult.id;
        } else {
          userTeamCodeResult = `team-spectator`;
          userTeamNameValue = userTeamCodeResult;
          userTeamIdValue = userTeamCodeResult;
        }

        setUserTeamName(userTeamNameValue);
        setUserTeamCode(userTeamCodeResult);
        setUserTeamId(userTeamIdValue);
      }
    }, [userData, activeChat, isUserOnSpectatorMode, roomTeams]);

    useEffect(() => {
      const handleShuffleVideoCall = (socketData) => {
        const { room_id, room_teams } = socketData;
        if (room_id?.toString() === roomId?.toString()) {
          updateRoomParticipantCall(room_teams);
        }
      };

      socket.on(
        SOCKET_EVENTS.SHUFFLE_VIDEO_CALL_IN_ROOM,
        handleShuffleVideoCall
      );
      return () =>
        socket.off(
          SOCKET_EVENTS.SHUFFLE_VIDEO_CALL_IN_ROOM,
          handleShuffleVideoCall
        );
    }, [socket, roomId, updateRoomParticipantCall]);

    useEffect(() => {
      socket.on(
        SOCKET_EVENTS.SEND_EMOJI_REACTION_IN_GROUP_ROOM,
        (socketData) => {
          const socketDataRoomId = socketData.room_id?.toString();
          const emojiReactionId = socketData.id?.toString();

          if (socketDataRoomId === roomId?.toString()) {
            delete socketData.room_id;

            setReactionEmojis((prev) => {
              const emojiExists = prev.some(
                (e) => e.id?.toString() === emojiReactionId
              );
              if (emojiExists) {
                return prev;
              }
              return [...prev, socketData];
            });

            setTimeout(() => {
              setReactionEmojis((prev) =>
                prev.filter((e) => e.id?.toString() !== emojiReactionId)
              );
            }, (socketData.duration || 4) * 1000);
          }
        }
      );

      return () => socket.off(SOCKET_EVENTS.SEND_EMOJI_REACTION_IN_GROUP_ROOM);
    }, [socket, roomId]);

    useEffect(() => {
      const intervalId = setInterval(() => {
        if (activeScreenshotId) {
          if (globalCountdown > 0) {
            setGlobalCountdown((prev) => prev - 1);
          } else {
            processQueue();
          }
        } else {
          processQueue();
        }
      }, 1000);

      return () => clearInterval(intervalId);
    }, [globalCountdown, activeScreenshotId, processQueue]);

    useEffect(() => {
      if (countdown > 0) {
        const timer = setTimeout(() => {
          setCountdown((prevCountdown) => {
            if (prevCountdown === 1) {
              if (currentToolboxScreenshot) {
                const { user_team } = currentToolboxScreenshot;
                if (user_team) {
                  reactToRoomScreenshotApiCall(roomId, {
                    screenshot_owner_id: user_team.team_representing_user_id,
                    type: "like",
                  });
                }
              }
              processQueue();
            }
            return prevCountdown - 1;
          });
        }, 1000);
        return () => clearTimeout(timer);
      }
    }, [
      countdown,
      currentToolboxScreenshot,
      roomId,
      reactToRoomScreenshotApiCall,
      processQueue,
    ]);

    useEffect(() => {
      socket.on(
        SOCKET_EVENTS.SEND_TOOLBOX_SCREENSHOT_IN_GROUP_ROOM,
        (screenshotData) => {
          const serverTime = Date.now() + (window.serverTimeDiff || 0);
          setToolboxScreenshotsQueue((prevQueue) => {
            const newScreenshot = {
              ...screenshotData,
              id: screenshotData.id || Date.now(),
              start_timestamp: screenshotData.start_timestamp || serverTime,
              end_timestamp:
                screenshotData.end_timestamp ||
                serverTime + screenshotData.duration_in_secs * 1000,
              duration_in_secs: screenshotData.duration_in_secs,
            };

            if (!prevQueue.some((item) => item.id === newScreenshot.id)) {
              const updatedQueue = [...prevQueue, newScreenshot].sort(
                (a, b) => a.start_timestamp - b.start_timestamp
              );
              if (prevQueue.length === 0) {
                processQueue();
              }
              return updatedQueue;
            }
            return prevQueue;
          });
        }
      );

      return () =>
        socket.off(SOCKET_EVENTS.SEND_TOOLBOX_SCREENSHOT_IN_GROUP_ROOM);
    }, [socket, processQueue]);

    useEffect(() => {
      if (toolboxScreenshotsData.length > 0) {
        const currentScreenshot =
          toolboxScreenshotsData[currentToolboxScreenshotIndex];
        if (currentScreenshot) {
          const timer = setTimeout(() => {
            closeToolboxScreenshot(currentScreenshot.id);
            if (
              currentToolboxScreenshotIndex <
              toolboxScreenshotsData.length - 1
            ) {
              setCurrentToolboxScreenshotIndex((prevIndex) => {
                const newIndex = prevIndex + 1;
                setCurrentToolboxScreenshot(toolboxScreenshotsData[newIndex]);
                return newIndex;
              });
            } else {
              setCurrentToolboxScreenshotIndex(0);
              setCurrentToolboxScreenshot(null);
            }
          }, currentScreenshot.duration_in_secs * 1000);

          return () => clearTimeout(timer);
        }
      } else {
        setCurrentToolboxScreenshotIndex(0);
        setCurrentToolboxScreenshot(null);
      }
    }, [
      toolboxScreenshotsData,
      currentToolboxScreenshotIndex,
      closeToolboxScreenshot,
    ]);

    useEffect(() => {
      socket.on(
        SOCKET_EVENTS.USER_CONNECTED_IN_GROUP_ROOM,
        handleUserConnected
      );
      socket.on(
        SOCKET_EVENTS.USER_DISCONNECTED_IN_GROUP_ROOM,
        handleUserDisconnected
      );

      return () => {
        socket.off(
          SOCKET_EVENTS.USER_CONNECTED_IN_GROUP_ROOM,
          handleUserConnected
        );
        socket.off(
          SOCKET_EVENTS.USER_DISCONNECTED_IN_GROUP_ROOM,
          handleUserDisconnected
        );
      };
    }, [socket, handleUserConnected, handleUserDisconnected]);

    useEffect(() => {
      if (!Object.keys(userData).length || !roomId || !userTeamCode) {
        if (myVideoStream.current) {
          myVideoStream.current.getTracks().forEach((track) => track.stop());
          myVideoStream.current = null;
        }
        return;
      }

      initializeMedia();

      return () => {
        if (myVideoStream.current) {
          myVideoStream.current.getTracks().forEach((track) => track.stop());
        }
      };
    }, [
      userData,
      roomId,
      userTeamCode,
      selectedAudioInputDevice,
      selectedVideoDevice,
    ]);

    if (!mediaSupported) {
      return (
        <div className={classes.errorContainer}>
          <Typography variant="h6">
            Video calls are not supported in your browser. Please use a modern
            browser like Chrome, Firefox, or Edge.
          </Typography>
        </div>
      );
    }

    return (
      <div className={classes.leftOuterBox}>
        <div className={classes.videoGrid}>
          <div className={classes.roomSpeakingTag}>
            {isSpeaking && "Speaking..."}
          </div>
        </div>

        <div className={classes.videoGrid} ref={videoGridRef}>
          <TopVideoWebCam
            classes={classes}
            classes2={classes2}
            isVideoOn={isVideoOn}
            isAudioOn={isAudioOn}
            myVideoRef={myVideoRef}
            handleRoomParticipantsVisibility={handleRoomParticipantsVisibility}
            handleVideoToggle={handleVideoToggle}
            handleAudioToggle={handleAudioToggle}
            showRoomParticipants={showRoomParticipants}
            handleWebcamMenuClick={handleWebcamMenuClick}
            anchorWebcamMenuEl={anchorWebcamMenuEl}
            handleWebcamMenuClose={handleWebcamMenuClose}
            openUserWebcamSettingsModal={openUserWebcamSettingsModal}
            handleRoomTitleToggle={handleRoomTitleToggle}
            handleRoomTeamsVisibility={handleRoomTeamsVisibility}
            showRoomTeams={showRoomTeams}
            showRoomTitle={showRoomTitle}
            userTeamCode={userTeamCode}
            userTeamName={userTeamName}
            userTeamScore={currentUserTeamScore}
            roomId={roomId}
            isUserOnSpectatorMode={isUserOnSpectatorMode}
            loadANewPage={loadANewPage}
            handleReactionIconExpandClick={handleReactionIconExpandClick}
            reactionIconExpanded={reactionIconExpanded}
            handleReactionIcon={handleReactionIcon}
            displayVideo={isUserOnSpectatorMode || !showMyVisual ? false : true}
            handleSendToolboxScreenshot={handleSendToolboxScreenshot}
            activeTeamParticipantVideos={activeTeamParticipantVideos}
            currentToolboxScreenshot={
              currentToolboxScreenshot?.has_team_selected &&
              currentToolboxScreenshot?.receiver_id?.toString() ===
                userData.id?.toString()
                ? currentToolboxScreenshot
                : null
            }
            countdown={globalCountdown}
            socket={socket}
          />

          {isUserOnSpectatorMode &&
          memoizedAllParticipantsVideos.length === 0 ? (
            <div className={classes.emptyVideoCamContainerForSpectators}>
              No ongoing live stream
            </div>
          ) : (
            memoizedAllParticipantsVideos
          )}
        </div>

        <UserWebcamSettings
          userWebcamSettingsOpen={userWebcamSettingsOpen}
          setUserWebcamSettingsOpen={setUserWebcamSettingsOpen}
          selectedAudioInputDevice={selectedAudioInputDevice}
          setSelectedAudioInputDevice={setSelectedAudioInputDevice}
          selectedAudioOutputDevice={selectedAudioOutputDevice}
          setSelectedAudioOutputDevice={setSelectedAudioOutputDevice}
          selectedVideoDevice={selectedVideoDevice}
          setSelectedVideoDevice={setSelectedVideoDevice}
          myVideoRef={myVideoRef}
          myVideoSettingsRef={myVideoSettingsRef}
        />

        <Modal
          aria-labelledby="transition-modal-title"
          aria-describedby="transition-modal-description"
          open={openReactionModal}
          onClose={handleCloseReactionModal}
          closeAfterTransition
          slots={{ backdrop: Backdrop }}
          slotProps={{
            backdrop: {
              timeout: 500,
            },
          }}
        >
          <Fade in={openReactionModal}>
            <Box sx={styleForReactionModal}>
              <Typography
                id="transition-modal-title"
                variant="h6"
                component="h2"
                textAlign={"center"}
              >
                React to{" "}
                {capitalizeEachFirstWord(selectedReactionData.username)}
              </Typography>
              <div className={classes.reactionOptions2}>
                <IconButton
                  aria-label="Like"
                  onClick={() => handleReactionIcon("👍", true)}
                >
                  <ThumbUpIcon className={classes2.customIconButton} />
                </IconButton>
                <IconButton
                  aria-label="Dislike"
                  onClick={() => handleReactionIcon("👎", true)}
                >
                  <ThumbDownIcon className={classes2.customIconButton} />
                </IconButton>
                <IconButton
                  aria-label="Love"
                  onClick={() => handleReactionIcon("❤️", true)}
                >
                  <FavoriteIcon className={classes2.customIconButton} />
                </IconButton>
                <IconButton
                  aria-label="Sad"
                  onClick={() => handleReactionIcon("💔", true)}
                >
                  <SentimentVeryDissatisfiedIcon
                    className={classes2.customIconButton}
                  />
                </IconButton>
              </div>
            </Box>
          </Fade>
        </Modal>

        {currentToolboxScreenshot &&
          !currentToolboxScreenshot?.has_team_selected && (
            <ToolboxScreenshotPopup
              screenshotData={currentToolboxScreenshot}
              handleClose={closeToolboxScreenshot}
            />
          )}

        <div className="emoji-container">
          {reactionEmojis.map((emojiObj, index) => {
            const style = {
              left: `calc(50% + ${emojiObj.offsetX || 0}px)`,
              animationDuration: `${emojiObj.duration || 4}s`,
              animationDelay: "0s",
              zIndex: 1001 + index,
            };

            // Determine animation class based on position
            let animationClass = "reaction-container";
            if (emojiObj.animationType === "left") {
              animationClass += " reaction-left";
            } else if (emojiObj.animationType === "right") {
              animationClass += " reaction-right";
            }

            return (
              <span
                key={emojiObj.id}
                className={animationClass}
                style={style}
                data-emoji={emojiObj.emoji}
              >
                <span className="emoji-icon">{emojiObj.emoji}</span>
                <span className="emoji-description">
                  {emojiObj.isSentToUser
                    ? `${emojiObj.username} → ${emojiObj.receiverUsername}`
                    : emojiObj.username}
                </span>
              </span>
            );
          })}
        </div>
      </div>
    );
  }
);

export default UserWebcam;
